// Utils
import { interpolateUrl } from '@/libs/utils/url';

// Constants

/** @const {string} EVENT_ASSETS_ENDPOINT the endpoint for getting the event's assets */
const EVENT_ASSETS_ENDPOINT = '{{node_url}}/api/v1/eid/{{eid}}/{{asset}}';

/** @const {string} EVENT_REG_ASSETS_ENDPOINT the endpoint for getting the event's assets */
const EVENT_REG_ASSETS_ENDPOINT = '/api/v1/appservice/assets/{{branding}}/config/{{path_name}}/{{asset}}';

/** @const {string} TEMPLATE_REG_ASSETS_ENDPOINT the endpoint for getting the event's assets */
const TEMPLATE_REG_ASSETS_ENDPOINT = '/api/v1/appservice/assets/config/template/{{eid}}/{{asset}}';

/** @const {string} STATIC_ASSETS_PATH the local app static assets path */
const STATIC_ASSETS_PATH = '{{assetPath}}/images/{{asset}}';

// Methods

/**
 * Given an asset name this method returns the url of the asset
 * relative to the root app.
 *
 * @param {string} asset the asset name
 * @param {string} branding the name of the branded app
 *
 * @returns {string} the compiled url to the root app asset
 */
export function getAssetSrc(asset, branding) {
    return transformUrl(window.APP.brandingAssetsUrl, asset, null, branding);
}

/**
 * Given an asset name this method returns the path to the
 * desired static asset.
 *
 * @param {string} asset the asset name
 *
 * @returns {string} the compiled URL to the desired asset
 */
export function getStaticAssetSrc(asset) {
    return interpolateUrl(STATIC_ASSETS_PATH, { assetPath: window.APP.assetsPath, asset });
}

/**
 * Builds the vue-meta link descriptor of an auth stylesheet, adding Subresource Integrity
 * attributes when the build provided a hash for it.
 *
 * @param {'auth'|'authV2'} name the stylesheet key in `window.APP.cssIntegrity`
 * @param {string} filename the stylesheet name without the asset suffix and extension
 *
 * @returns {object} the link descriptor
 */
export function getStylesheetLink(name, filename) {
    const { assetsPath, assetSuffix, cssIntegrity } = window.APP;
    const integrity = cssIntegrity?.[name];
    const link = { vmid: 'main-style', rel: 'stylesheet', href: `${assetsPath}/css/${filename}${assetSuffix}.css` };

    return integrity ? { ...link, integrity, crossorigin: 'anonymous' } : link;
}

/**
 * Given an asset name and the event object, this method
 * build and returns the URL to the requested resource.
 *
 * @param {InvitationEvent} event
 * @param {string} asset the asset name
 *
 * @returns {string} the compiled URL to the desired asset
 */
export function getEventAssetSrc(asset, event) {
    return transformUrl(EVENT_ASSETS_ENDPOINT, asset, event);
}

/**
 * Given an asset name, branding, and the event object, this method
 * build and returns the URL to the requested resource specific
 * for the registration page.
 *
 * @param {string} asset the asset name
 * @param {string} branding the name of the branded app
 * @param {InvitationEvent} event
 *
 * @returns {string} the compiled URL to the desired asset
 */
export function getBrandingAssetSrc(asset, branding, event) {
    return transformUrl(EVENT_REG_ASSETS_ENDPOINT, asset, event, branding);
}

/**
 * Given an asset name, branding, and the template eid, this method
 * build and returns the URL to the requested resource specific
 * for the registration page.
 *
 * @param {string} asset the asset name
 * @param {string} eid the eid of the template
 *
 * @returns {string} the compiled URL to the desired asset
 */
export function getTemplateRegAssetSrc(asset, eid) {
    return interpolateUrl(TEMPLATE_REG_ASSETS_ENDPOINT, { asset, eid });
}

/**
 * Given an uncompiled URL, a branding, asset name and an optional event, this method
 * reconstructs the correct asset URL.
 *
 * @param {string} url the uncompiled base URL
 * @param {string} asset the asset name
 * @param {InvitationEvent} [event] an event object
 * @param {string} [branding] the branding name
 *
 * @returns {string} the compiled URL to the desired asset
 */
export function transformUrl(url, asset, event, branding) {
    if (event) {
        event.eid = event.eid || event._id;

        // Backward compat
        event.node = event.node || event.node_url;
        return interpolateUrl(url, { ...event, asset, branding });
    }

    return interpolateUrl(url, { asset, branding });
}
